import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  Spinner,
  Label,
  Row,
  Col,
  Button,
} from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import styles from "../css/auditlog.module.scss";
import { useUserAssignmentService } from "modules/Library/Users/api/useUserAssignmentQuery";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import { getAuditFormFields } from "../helper/auditconstant";
import { AuditLogFormValues } from "../helper/audit.types";
import { GetUserData } from "common/service/CommonService";

const AuditLog: React.FC = () => {
  document.title = "Audit Log | ZENTIXS VISTA";
  
  const [loading, setLoading] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Fetch Users for Dropdown
  const { UserDropdownQuery } = useUserAssignmentService({isuserdropdownenabled:true});

  const {
    ModuleDropdownQuery,
    SelectAllMutation,
    RecordDropdownMutation,
    ChildModuleDropdownMutation,
  } = useAuditService(true);
  const userData = useMemo(() => GetUserData(), []);

  const moduleOptions = useMemo(() => {
    return (ModuleDropdownQuery?.data?.data ?? []).map((m: any) => ({
      label: m.parentmodulename,
      value: m.parentmodulename,
    }));
  }, [ModuleDropdownQuery.data]);

  const userOptions = useMemo(() => {
    return (UserDropdownQuery?.data?.data ?? []).map((u: any) => ({
      label: u.userdisplayname,
      value: u.userid,
    }));
  }, [UserDropdownQuery.data]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<AuditLogFormValues>({
    defaultValues: {
      from: null,
      to: null,
      module: "",
      submodule: "",
      record: "",
      operation: "",
      user: "",
    },
  });

  const selectedModule = watch("module");
  const selectedSubModule = watch("submodule");

  const subModuleOptions = useMemo(() => {
    if (!selectedModule) return [];
    return (ChildModuleDropdownMutation?.data?.data ?? []).map((m: any) => ({
      label: m.modulename,
      value: m.modulename,
    }));
  }, [ChildModuleDropdownMutation.data, selectedModule]);

  const recordOptions = useMemo(() => {
    if (!selectedSubModule) return [];
    return (RecordDropdownMutation?.data?.data ?? []).map((r: any) => ({
      label: r.recordname,
      value: r.recordid,
    }));
  }, [RecordDropdownMutation.data, selectedSubModule]);

  useEffect(() => {
    if (selectedModule) {
      const payload = {
        modulename: selectedModule,
        searchtext: "",
      };
      ChildModuleDropdownMutation.mutate(payload);
    }
    setValue("submodule", "");
    setValue("record", "");
  }, [selectedModule]);

  useEffect(() => {
    if (selectedSubModule) {
      const payload = {
        modulename: selectedSubModule,
        searchtext: "",
      };
      RecordDropdownMutation.mutate(payload);
    }
    setValue("record", "");
  }, [selectedSubModule]);

  const onSubmit = (values: any) => {
    const selectedRecord = recordOptions.find(
      (r: any) => r.value === values.record,
    );

    const payload = {
      fromdate: getValues("from"),
      todate: getValues("to"),
      modulename: values.submodule,
      recordid:
        ["Volunteer Alcohol Test", "Study Alcohol Test", "Study Document", "Subject Label", "IdCard Label", "Vacutainer Label", "Cryovial Label", "Dispensing Label","Study"].includes(values.submodule)
          ? null
          : values.record || null,

      refid:
        ["Volunteer Alcohol Test", "Study Alcohol Test", "IdCard Label", "Vacutainer Label", "Cryovial Label", "Dispensing Label"].includes(values.submodule)
          ? values.record || null
          : null,

      studyid:
        ["Study","Study Document"].includes(values.submodule)
          ? values.record || null
          : null,
      operationtype: values.operation || null,
      createdby: values.user || null,
    };

    setLoading(true);
    SelectAllMutation.mutate(payload, {
      onSuccess: (data) => {
        setLoading(false);
        setIsGenerated(true);

        // Create Blob URL from PDF binary data
        if (data instanceof Blob) {
          const blobUrl = URL.createObjectURL(data);
          setPdfUrl(blobUrl);
        }
      },
      onError: (error) => {
        setLoading(false);
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const handleReset = () => {
    reset({
      from: null,
      to: null,
      module: "",
      submodule: "",
      record: "",
      user: "",
    });
    setIsGenerated(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  // Cleanup Blob URL on unmount
  React.useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "Audit_Log_Report.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl);
      if (printWindow) {
        printWindow.print();
      }
    }
  };

  const fromDateValue = watch("from");
  const formFields = useMemo(
    () =>
      getAuditFormFields(
        moduleOptions,
        userOptions,
        recordOptions,
        subModuleOptions,
        fromDateValue,
        selectedModule,
        selectedSubModule,
        ChildModuleDropdownMutation.isPending,
        RecordDropdownMutation.isPending,
      ),
    [
      moduleOptions,
      userOptions,
      recordOptions,
      subModuleOptions,
      fromDateValue,
      selectedModule,
      selectedSubModule,
      ChildModuleDropdownMutation.isPending,
      RecordDropdownMutation.isPending,
    ],
  );

  const firstRowFields = useMemo(
    () => formFields.filter((f) => ["from", "to", "operation", "user"].includes(f.name)),
    [formFields],
  );

  const secondRowFields = useMemo(
    () => formFields.filter((f) => ["module", "submodule", "record"].includes(f.name)),
    [formFields],
  );

  return (
    <Container fluid className={`p-0 ${styles.mainContainer}`}>
      <div className={styles.pageWrapper}>
        <Card className={styles.horizontalCard}>
          <CardBody className={styles.horizontalFormContainer}>
            <div className="w-100 d-flex flex-column gap-3">
              {/* First Row (Full Width) */}
              <CommonForm
                fields={firstRowFields}
                control={control}
                errors={errors}
                columns={1}
                parentClass={"px-3"}
                childClass={"px-0"}
                setValue={setValue}
                getValues={getValues}
              />

              {/* Second Row + Action Buttons */}
              <Row className="g-2 w-100 align-items-stretch">
                <div className={styles.auditFormContainer}>
                  <CommonForm
                    fields={secondRowFields}
                    control={control}
                    errors={errors}
                    columns={1}
                    parentClass={"px-3"}
                    childClass={"px-0"}
                    setValue={setValue}
                    getValues={getValues}
                  />
                </div>

                {/* Action Column */}
                <div className={styles.auditActionContainer}>
                  <div className={styles.actionWrapper}>
                    <Button
                      outline
                      color="primary"
                      className={styles.iconBtn}
                      onClick={handleSubmit(onSubmit)}
                      disabled={loading}
                      title="Generate PDF"
                    >
                      {loading ? (
                        <Spinner size="sm" />
                      ) : (
                        <i className="ri-file-pdf-line"></i>
                      )}
                    </Button>

                    <Button
                      outline
                      className={styles.iconBtn}
                      onClick={handleReset}
                      title="Reset"
                    >
                      <i className="ri-refresh-line"></i>
                    </Button>
                  </div>
                </div>
              </Row>
            </div>
          </CardBody>
        </Card>

        {/* Conditional Content: PDF Preview or Empty State */}
        {isGenerated ? (
          <div className="mt-4 w-100">
            <div className={styles.pdfHeader}>
              <div className="d-flex align-items-center gap-2">
                <i className="ri-file-pdf-line text-white fs-4"></i>
                <h6 className="m-0 fw-bold text-white">
                  Audit_Log_Report.pdf
                </h6>
              </div>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  color="light"
                  outline
                  className={styles.pdfActionBtn}
                  onClick={handleDownload}
                >
                  <i className="ri-download-line me-1"></i> Download
                </Button>
                {/* <Button size="sm" color="light" outline className={styles.pdfActionBtn} onClick={handlePrint}>
                  <i className="ri-printer-line me-1"></i> Print
                </Button> */}
              </div>
            </div>
            <Card className={styles.pdfPreviewCard}>
              <CardBody className={styles.pdfPreviewBody}>
                <div className={styles.pdfIframeWrapper}>
                  <iframe
                    src={`${pdfUrl}#toolbar=0&navpanes=0`}
                    width="100%"
                    height="100%"
                    title="Audit Log Report"
                    className={styles.pdfIframe}
                  />
                </div>
              </CardBody>
            </Card>
          </div>
        ) : (
          /* Empty State Card */
          <Card className={`mt-4 ${styles.horizontalCard}`}>
            <CardBody className={styles.emptyStateBody}>
              <div className={styles.emptyStateContainer}>
                <div className={styles.emptyStateIconWrapper}>
                  <i
                    className="ri-history-line text-primary"
                    style={{ fontSize: "2.5rem" }}
                  ></i>
                </div>
                <h5 className={styles.emptyStateTitle}>No Audit Logs Found</h5>
                <p className={styles.emptyStateText}>
                  The audit log report hasn't been generated yet. <br />
                  Please configure your filters and click{" "}
                  <strong>"Generate"</strong> to start.
                </p>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </Container>
  );
};

export default AuditLog;
